import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import config from "../../config";
import Navbar from "../../components/public/Navbar";
import Footer from "../../components/public/Footer";

const FALLBACK_ARTICLES = [
  {
    id: "cold-chain",
    slug: "why-cold-chain-matters",
    title: "Why cold-chain matters for home vaccination",
    excerpt: "How BookMyJab keeps vaccines within validated temperature ranges until the moment of administration.",
    author: "BookMyJab Clinical Team",
    body: "Vaccines are biological products. Maintaining the cold chain from warehouse to your living room is non-negotiable.\n\nOur nurses travel with validated cold-chain kits, and every vial is checked before administration. If a break is suspected, the dose is discarded — never administered.",
  },
  {
    id: "family-schedule",
    slug: "family-vaccination-schedule-gurgaon",
    title: "Building a family vaccination schedule in Gurgaon",
    excerpt: "A practical way to keep kids, adults and elders protected without clinic queues.",
    author: "BookMyJab Concierge",
    body: "Start with age-appropriate catch-up vaccines, then add annual flu and travel shots as needed.\n\nUse your BookMyJab profile to store family members and book the next window before the previous one expires.",
  },
];

const FALLBACK_CAREERS = [
  {
    id: "nurse-ggn",
    title: "Home Vaccination Nurse — Gurgaon",
    location: "Gurgaon",
    type: "Full-time / contract",
    description: "BSc/GNM nurses with vaccination experience. Soft skills and cold-chain discipline required.",
  },
];

export function ArticlesPage() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${config.API}/articles`).then(({ data }) => {
      setItems(data.items?.length ? data.items : FALLBACK_ARTICLES);
    }).catch(() => setItems(FALLBACK_ARTICLES));
  }, []);
  return (
    <div className="min-h-screen bg-[#fdfcfb]" data-testid="articles-page">
      <Navbar />
      <section className="pt-28 pb-16 max-w-7xl mx-auto px-4">
        <p className="text-xs font-bold uppercase tracking-widest text-[#f37721] mb-3">Journal</p>
        <h1 className="text-4xl font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>Articles</h1>
        <p className="mt-3 text-[#673c96] max-w-2xl">Clinical notes and concierge guides for safer at-home vaccination.</p>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {items.map((a) => (
            <Link key={a.id} to={`/articles/${a.slug || a.id}`} className="bg-white rounded-3xl p-6 border border-[#673c96]/10 hover:shadow-md transition-shadow" data-testid={`article-card-${a.id}`}>
              <h2 className="text-xl font-semibold text-[#311b52]">{a.title}</h2>
              <p className="mt-2 text-sm text-[#673c96]">{a.excerpt}</p>
              <span className="mt-4 inline-block text-sm font-semibold text-[#f37721]">Read article →</span>
            </Link>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}

export function ArticleDetailPage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  useEffect(() => {
    axios.get(`${config.API}/articles/${slug}`).then(({ data }) => setArticle(data)).catch(() => {
      setArticle(FALLBACK_ARTICLES.find((a) => a.slug === slug || a.id === slug) || null);
    });
  }, [slug]);
  return (
    <div className="min-h-screen bg-[#fdfcfb]">
      <Navbar />
      <article className="pt-28 pb-16 max-w-3xl mx-auto px-4">
        {!article ? (
          <p className="text-[#673c96]">Article not found.</p>
        ) : (
          <>
            <p className="text-xs font-bold uppercase tracking-widest text-[#f37721] mb-3">Journal</p>
            <h1 className="text-4xl font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>{article.title}</h1>
            <p className="mt-2 text-sm text-[#673c96]">{article.author}</p>
            <div className="mt-8 text-[#3e195e] whitespace-pre-wrap leading-relaxed" style={{ fontFamily: "Montserrat" }}>{article.body}</div>
            <Link to="/vaccination-catalogue" className="inline-block mt-10 bg-[#f37721] text-white rounded-full px-6 py-3 font-semibold">Book now</Link>
          </>
        )}
        <Link to="/articles" className="inline-block mt-6 text-[#f37721] font-semibold">← Back to articles</Link>
      </article>
      <Footer />
    </div>
  );
}

export function CareersPage() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${config.API}/careers`).then(({ data }) => {
      setItems(data.items?.length ? data.items : FALLBACK_CAREERS);
    }).catch(() => setItems(FALLBACK_CAREERS));
  }, []);
  return (
    <div className="min-h-screen bg-[#fdfcfb]" data-testid="careers-page">
      <Navbar />
      <section className="pt-28 pb-16 max-w-7xl mx-auto px-4">
        <p className="text-xs font-bold uppercase tracking-widest text-[#f37721] mb-3">Careers</p>
        <h1 className="text-4xl font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>Join BookMyJab</h1>
        <p className="mt-3 text-[#673c96] max-w-2xl">Nurses, ops and clinical partners building premium at-home care in Gurgaon.</p>
        <div className="mt-10 space-y-4">
          {items.map((j) => (
            <div key={j.id} className="bg-white rounded-2xl p-6 border border-[#673c96]/10">
              <h2 className="text-xl font-semibold text-[#311b52]">{j.title}</h2>
              <p className="text-sm text-[#673c96] mt-1">{j.location} · {j.type}</p>
              <p className="mt-3 text-[#3e195e]">{j.description}</p>
              <a href={`mailto:${config.SUPPORT_EMAIL || "support@bookmyjab.com"}?subject=${encodeURIComponent("Application: " + j.title)}`}
                className="inline-block mt-4 text-[#f37721] font-semibold">Apply via email →</a>
            </div>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}

export function SupportPage() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    axios.get(`${config.API}/support/info`).then(({ data }) => setInfo(data)).catch(() => {});
  }, []);
  const wa = info?.whatsapp || config.WHATSAPP_NUMBER || "919873554343";
  return (
    <div className="min-h-screen bg-[#fdfcfb]" data-testid="support-page">
      <Navbar />
      <section className="pt-28 pb-16 max-w-3xl mx-auto px-4">
        <p className="text-xs font-bold uppercase tracking-widest text-[#f37721] mb-3">Support</p>
        <h1 className="text-4xl font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>We're here</h1>
        <p className="mt-3 text-[#673c96]">{info?.hours || "Concierge hours: 8am – 8pm IST"}</p>
        <div className="mt-8 grid gap-4">
          <a href={`https://wa.me/${wa}`} className="block bg-[#311b52] text-white rounded-2xl px-6 py-4 font-semibold text-center">WhatsApp Concierge</a>
          <a href={`tel:${(info?.phone || config.SUPPORT_PHONE || "").replace(/\s/g, "")}`} className="block bg-white border border-[#673c96]/20 rounded-2xl px-6 py-4 text-center text-[#311b52] font-semibold">
            Call {info?.phone || "+91 98735 54343"}
          </a>
          <a href={`mailto:${info?.email || "support@bookmyjab.com"}`} className="block text-center text-[#f37721] font-semibold">
            {info?.email || "support@bookmyjab.com"}
          </a>
          <div className="grid sm:grid-cols-2 gap-3 pt-2">
            <Link to="/vaccination-catalogue" className="text-center rounded-full bg-[#f37721] text-white py-3 font-semibold sm:col-span-2">Book now</Link>
          </div>
          <Link to="/grievance" className="block text-center text-sm text-[#673c96]">Grievance redressal →</Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
